import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Building2,
  PlusCircle,
  Edit,
  Layers,
  RefreshCw,
  Phone,
  Mail,
  AlertTriangle,
  Percent,
  Clock,
  User,
} from "lucide-react";
import { Modal, Input, InputNumber, Select, Switch, message } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import { dsaApi } from "../services/api";

// Lender type badge styling — BANK / NBFC / FINTECH
const LENDER_TYPES = [
  { value: "BANK", label: "Bank" },
  { value: "NBFC", label: "NBFC" },
  { value: "FINTECH", label: "Fintech" },
];

const TYPE_CONFIG = {
  BANK: { label: "BANK", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  NBFC: { label: "NBFC", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  FINTECH: { label: "FINTECH", bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
};

// DSA me lender ke paas ye loan products ho sakte hain
const LOAN_TYPES = [
  "PERSONAL",
  "BUSINESS",
  "HOME",
  "LAP",
  "VEHICLE",
  "EDUCATION",
  "GOLD",
  "MSME",
];

const formatINR = (val) => {
  if (val === undefined || val === null || val === "" || isNaN(val)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(val));
};

// Backend kabhi {content:[]}, kabhi {data:[]}, kabhi seedha array bhejta hai
const toList = (res) => {
  if (Array.isArray(res)) return res;
  const inner = res?.content || res?.data || res?.lenders || res?.products;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(inner?.content)) return inner.content;
  return [];
};

// Phone 10 digit ka aur 6-9 se start hona chahiye
const isValidPhone = (val) => /^[6-9]\d{9}$/.test(String(val || "").trim());
const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(val || "").trim());

const EMPTY_LENDER = {
  lenderName: "",
  lenderType: "BANK",
  contactPerson: "",
  phone: "",
  email: "",
  payoutTat: null,
  notes: "",
  active: true,
};

const EMPTY_PRODUCT = {
  productName: "",
  loanType: "PERSONAL",
  minInterestRate: null,
  maxInterestRate: null,
  minAmount: null,
  maxAmount: null,
  minTenure: null,
  maxTenure: null,
  commissionPercentage: null,
  minPayout: null,
  maxPayout: null,
  active: true,
};

// Range ko "a – b" me dikhana, ek hi value ho to wahi
const renderRange = (min, max, suffix = "") => {
  const hasMin = min !== undefined && min !== null && min !== "";
  const hasMax = max !== undefined && max !== null && max !== "";
  if (!hasMin && !hasMax) return "—";
  if (hasMin && hasMax) return `${min}${suffix} – ${max}${suffix}`;
  return `${hasMin ? min : max}${suffix}`;
};

export default function DsaLenders() {
  const { currentTheme } = useTheme();
  const primaryColor = currentTheme?.color || "#2e856e";
  const { has } = usePermissions();

  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingLender, setSavingLender] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL"); // ALL | ACTIVE | INACTIVE

  // Lender add/edit modal
  const [lenderModal, setLenderModal] = useState({ open: false, mode: "create", id: null, form: EMPTY_LENDER });

  // Products panel (modal) — ek lender ke products
  const [productsPanel, setProductsPanel] = useState({ open: false, lender: null });
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);

  // Product add/edit modal
  const [productModal, setProductModal] = useState({ open: false, mode: "create", id: null, form: EMPTY_PRODUCT });
  const [savingProduct, setSavingProduct] = useState(false);

  // Permission codes — routePermissions.js me DSA_LENDER_VIEW already hai
  const canCreate = has("DSA_LENDER_CREATE");
  const canEdit = has("DSA_LENDER_EDIT");

  // Har keystroke pe API call na ho
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchLenders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (activeFilter === "ACTIVE") params.active = true;
      if (activeFilter === "INACTIVE") params.active = false;
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await dsaApi.getLenders(params);
      setLenders(toList(res));
    } catch (err) {
      console.error("Error fetching lenders:", err);
      // Endpoint abhi proposed hai — fail hone par honest error dikhao, mock data nahi
      setError(err?.message || err?.error || "Failed to load lenders. Please try again.");
      setLenders([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, debouncedSearch]);

  useEffect(() => {
    fetchLenders();
  }, [fetchLenders]);

  // Safety net — backend filter ignore kare to bhi list sahi dikhe
  const visibleLenders = lenders.filter((l) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch =
      term === "" ||
      l.lenderName?.toLowerCase().includes(term) ||
      l.name?.toLowerCase().includes(term) ||
      l.contactPerson?.toLowerCase().includes(term) ||
      String(l.phone || "").includes(term) ||
      l.email?.toLowerCase().includes(term);

    const isActive = l.active !== undefined ? !!l.active : l.status === true || l.status === "ACTIVE";
    const matchesActive =
      activeFilter === "ALL" ||
      (activeFilter === "ACTIVE" && isActive) ||
      (activeFilter === "INACTIVE" && !isActive);

    return matchesSearch && matchesActive;
  });

  // ── Lender create / edit ──
  const openCreateLender = () => {
    setLenderModal({ open: true, mode: "create", id: null, form: { ...EMPTY_LENDER } });
  };

  const openEditLender = (lender) => {
    setLenderModal({
      open: true,
      mode: "edit",
      id: lender.id ?? lender.lenderId,
      form: {
        lenderName: lender.lenderName || lender.name || "",
        lenderType: lender.lenderType || lender.type || "BANK",
        contactPerson: lender.contactPerson || "",
        phone: lender.phone || "",
        email: lender.email || "",
        payoutTat: lender.payoutTat ?? lender.payoutTatDays ?? null,
        notes: lender.notes || "",
        active: lender.active !== undefined ? !!lender.active : true,
      },
    });
  };

  const setLenderField = (key, value) =>
    setLenderModal((prev) => ({ ...prev, form: { ...prev.form, [key]: value } }));

  const handleSaveLender = async () => {
    const f = lenderModal.form;
    if (!f.lenderName.trim()) {
      message.warning("Please enter the lender name");
      return;
    }
    if (!f.lenderType) {
      message.warning("Please select the lender type");
      return;
    }
    if (f.phone && !isValidPhone(f.phone)) {
      message.warning("Phone must be 10 digits and start with 6-9");
      return;
    }
    if (f.email && !isValidEmail(f.email)) {
      message.warning("Please enter a valid email address");
      return;
    }

    const payload = {
      lenderName: f.lenderName.trim(),
      lenderType: f.lenderType,
      contactPerson: f.contactPerson.trim(),
      phone: String(f.phone || "").trim(),
      email: f.email.trim(),
      payoutTat: f.payoutTat,
      notes: f.notes.trim(),
      active: !!f.active,
    };

    setSavingLender(true);
    try {
      // Success message sirf API pass hone ke baad — fail hua to catch me jayega
      if (lenderModal.mode === "edit") {
        await dsaApi.updateLender(lenderModal.id, payload);
        message.success("Lender updated successfully");
      } else {
        await dsaApi.createLender(payload);
        message.success("Lender created successfully");
      }
      setLenderModal({ open: false, mode: "create", id: null, form: EMPTY_LENDER });
      fetchLenders();
    } catch (err) {
      console.error("Save lender failed:", err);
      message.error(err?.message || err?.error || "Failed to save lender");
    } finally {
      setSavingLender(false);
    }
  };

  // ── Status toggle ──
  const handleToggleStatus = async (lender) => {
    const id = lender.id ?? lender.lenderId;
    if (!id) return;
    setTogglingId(id);
    try {
      await dsaApi.toggleLenderStatus(id);
      message.success("Lender status updated");
      fetchLenders();
    } catch (err) {
      console.error("Toggle lender status failed:", err);
      message.error(err?.message || err?.error || "Failed to update lender status");
    } finally {
      setTogglingId(null);
    }
  };

  // ── Products panel ──
  const fetchProducts = useCallback(async (lenderId) => {
    setProductsLoading(true);
    setProductsError(null);
    try {
      const res = await dsaApi.getLenderProducts(lenderId);
      setProducts(toList(res));
    } catch (err) {
      console.error("Error fetching lender products:", err);
      setProductsError(err?.message || err?.error || "Failed to load products for this lender.");
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const openProducts = (lender) => {
    setProductsPanel({ open: true, lender });
    setProducts([]);
    fetchProducts(lender.id ?? lender.lenderId);
  };

  const closeProducts = () => {
    setProductsPanel({ open: false, lender: null });
    setProducts([]);
    setProductsError(null);
  };

  const openCreateProduct = () => {
    setProductModal({ open: true, mode: "create", id: null, form: { ...EMPTY_PRODUCT } });
  };

  const openEditProduct = (product) => {
    setProductModal({
      open: true,
      mode: "edit",
      id: product.id ?? product.productId,
      form: {
        productName: product.productName || product.name || "",
        loanType: product.loanType || "PERSONAL",
        minInterestRate: product.minInterestRate ?? null,
        maxInterestRate: product.maxInterestRate ?? null,
        minAmount: product.minAmount ?? null,
        maxAmount: product.maxAmount ?? null,
        minTenure: product.minTenure ?? null,
        maxTenure: product.maxTenure ?? null,
        commissionPercentage: product.commissionPercentage ?? null,
        minPayout: product.minPayout ?? null,
        maxPayout: product.maxPayout ?? null,
        active: product.active !== undefined ? !!product.active : true,
      },
    });
  };

  const setProductField = (key, value) =>
    setProductModal((prev) => ({ ...prev, form: { ...prev.form, [key]: value } }));

  const handleSaveProduct = async () => {
    const f = productModal.form;
    const lenderId = productsPanel.lender?.id ?? productsPanel.lender?.lenderId;
    if (!lenderId) return;

    if (!f.productName.trim()) {
      message.warning("Please enter the product name");
      return;
    }
    if (!f.loanType) {
      message.warning("Please select the loan type");
      return;
    }
    // Commission decimal allowed hai (1.25%) par 0-100 ke beech hi
    if (f.commissionPercentage === null || f.commissionPercentage === undefined || f.commissionPercentage === "") {
      message.warning("Please enter the commission percentage");
      return;
    }
    const commission = Number(f.commissionPercentage);
    if (isNaN(commission) || commission < 0 || commission > 100) {
      message.warning("Commission percentage must be between 0 and 100");
      return;
    }
    if (f.minInterestRate !== null && f.maxInterestRate !== null && Number(f.minInterestRate) > Number(f.maxInterestRate)) {
      message.warning("Minimum interest rate cannot be greater than the maximum");
      return;
    }
    if (f.minAmount !== null && f.maxAmount !== null && Number(f.minAmount) > Number(f.maxAmount)) {
      message.warning("Minimum amount cannot be greater than the maximum");
      return;
    }
    if (f.minTenure !== null && f.maxTenure !== null && Number(f.minTenure) > Number(f.maxTenure)) {
      message.warning("Minimum tenure cannot be greater than the maximum");
      return;
    }
    if (f.minPayout !== null && f.maxPayout !== null && Number(f.minPayout) > Number(f.maxPayout)) {
      message.warning("Minimum payout cannot be greater than the maximum");
      return;
    }

    const payload = {
      productName: f.productName.trim(),
      loanType: f.loanType,
      minInterestRate: f.minInterestRate,
      maxInterestRate: f.maxInterestRate,
      minAmount: f.minAmount,
      maxAmount: f.maxAmount,
      minTenure: f.minTenure,
      maxTenure: f.maxTenure,
      commissionPercentage: commission,
      minPayout: f.minPayout,
      maxPayout: f.maxPayout,
      active: !!f.active,
    };

    setSavingProduct(true);
    try {
      if (productModal.mode === "edit") {
        await dsaApi.updateLenderProduct(lenderId, productModal.id, payload);
        message.success("Product updated successfully");
      } else {
        await dsaApi.createLenderProduct(lenderId, payload);
        message.success("Product added successfully");
      }
      setProductModal({ open: false, mode: "create", id: null, form: EMPTY_PRODUCT });
      fetchProducts(lenderId);
      fetchLenders(); // products count refresh ho jaye
    } catch (err) {
      console.error("Save lender product failed:", err);
      message.error(err?.message || err?.error || "Failed to save product");
    } finally {
      setSavingProduct(false);
    }
  };

  // Products count backend se aaye to dikhao, warna "—" (guess mat karo)
  const productCountOf = (lender) => {
    const count = lender.productsCount ?? lender.productCount ?? (Array.isArray(lender.products) ? lender.products.length : undefined);
    return count === undefined || count === null ? "—" : count;
  };

  return (
    <div className="space-y-6 pb-12">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
            style={{ backgroundColor: primaryColor }}
          >
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Lender Master</h1>
            <p className="text-xs md:text-sm text-gray-500">
              Partner banks, NBFCs and fintechs where loan files are sourced, with their products and commission slabs
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchLenders}
            disabled={loading}
            className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all shadow-sm"
            title="Refresh List"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>

          {canCreate && (
            <button
              onClick={openCreateLender}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#2e856e] text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95"
              style={{ backgroundColor: primaryColor }}
            >
              <PlusCircle className="w-4 h-4" />
              <span>Add Lender</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="bg-white p-4 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {/* Search Box */}
          <div className="relative md:col-span-2">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search lender, contact person, phone, email..."
              className="w-full pl-10 pr-4 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
            />
          </div>

          {/* Active / Inactive */}
          <div>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full px-3 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e] font-medium text-gray-700"
            >
              <option value="ALL">All Lenders</option>
              <option value="ACTIVE">Active Only</option>
              <option value="INACTIVE">Inactive Only</option>
            </select>
          </div>

          {/* Reset */}
          <div className="flex items-center">
            <button
              onClick={() => {
                setSearchTerm("");
                setActiveFilter("ALL");
              }}
              className="w-full px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* ── Lenders Table ── */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-500">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
            <p className="text-sm font-semibold">Loading lenders...</p>
          </div>
        ) : error ? (
          // Honest error state — koi dummy row nahi dikhayenge
          <div className="py-16 px-6 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-500 opacity-80" />
            <p className="font-bold text-gray-800">Could not load lenders</p>
            <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">{error}</p>
            <button
              onClick={fetchLenders}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-[#2e856e] text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95"
              style={{ backgroundColor: primaryColor }}
            >
              <RefreshCw className="w-4 h-4" />
              <span>Retry</span>
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                  <th className="py-4 px-4">Lender Name</th>
                  <th className="py-4 px-4">Type</th>
                  <th className="py-4 px-4">Contact Person</th>
                  <th className="py-4 px-4">Phone / Email</th>
                  <th className="py-4 px-4 text-center">Products</th>
                  <th className="py-4 px-4 text-center">Status</th>
                  <th className="py-4 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleLenders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-gray-400">
                      <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-semibold">No lenders found</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Add a partner bank or NBFC, or try changing the filters
                      </p>
                    </td>
                  </tr>
                ) : (
                  visibleLenders.map((lender) => {
                    const id = lender.id ?? lender.lenderId;
                    const type = lender.lenderType || lender.type;
                    const typeInfo = TYPE_CONFIG[type] || {
                      label: type || "—",
                      bg: "bg-gray-100",
                      text: "text-gray-700",
                      border: "border-gray-200",
                    };
                    const isActive =
                      lender.active !== undefined ? !!lender.active : lender.status === true || lender.status === "ACTIVE";

                    return (
                      <tr key={id} className="hover:bg-gray-50/60 transition-colors group">
                        {/* Lender Name */}
                        <td className="py-3.5 px-4">
                          <div className="font-semibold text-gray-900">
                            {lender.lenderName || lender.name || "—"}
                          </div>
                          {lender.createdAt && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              Added {dayjs(lender.createdAt).format("DD/MM/YYYY")}
                            </div>
                          )}
                        </td>

                        {/* Type */}
                        <td className="py-3.5 px-4">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ${typeInfo.bg} ${typeInfo.text} ${typeInfo.border}`}
                          >
                            {typeInfo.label}
                          </span>
                        </td>

                        {/* Contact Person */}
                        <td className="py-3.5 px-4 text-sm text-gray-700">
                          {lender.contactPerson ? (
                            <span className="inline-flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5 text-gray-400" />
                              {lender.contactPerson}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        {/* Phone / Email */}
                        <td className="py-3.5 px-4">
                          {lender.phone && (
                            <div className="text-xs text-gray-700 flex items-center gap-1">
                              <Phone className="w-3 h-3 text-gray-400" />
                              <span>{lender.phone}</span>
                            </div>
                          )}
                          {lender.email && (
                            <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                              <Mail className="w-3 h-3" />
                              <span>{lender.email}</span>
                            </div>
                          )}
                          {!lender.phone && !lender.email && <span className="text-gray-400 text-xs">—</span>}
                        </td>

                        {/* Products count */}
                        <td className="py-3.5 px-4 text-center">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700 text-xs font-bold">
                            <Layers className="w-3.5 h-3.5" />
                            {productCountOf(lender)}
                          </span>
                        </td>

                        {/* Status toggle */}
                        <td className="py-3.5 px-4 text-center">
                          <Switch
                            checked={isActive}
                            loading={togglingId === id}
                            disabled={!canEdit}
                            onChange={() => handleToggleStatus(lender)}
                            style={isActive ? { backgroundColor: primaryColor } : undefined}
                          />
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            {canEdit && (
                              <button
                                onClick={() => openEditLender(lender)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                title="Edit Lender"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => openProducts(lender)}
                              className="px-2.5 py-1 text-xs font-bold text-[#2e856e] bg-[#2e856e]/5 hover:bg-[#2e856e]/10 rounded-lg border border-[#2e856e]/30 transition-all"
                              title="View products & commission slabs"
                            >
                              View Products
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Add / Edit Lender Modal ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-gray-900 font-bold text-base">
            <Building2 className="w-5 h-5 text-[#2e856e]" />
            <span>{lenderModal.mode === "edit" ? "Edit Lender" : "Add Lender"}</span>
          </div>
        }
        open={lenderModal.open}
        onOk={handleSaveLender}
        onCancel={() => setLenderModal({ open: false, mode: "create", id: null, form: EMPTY_LENDER })}
        okText={lenderModal.mode === "edit" ? "Save Changes" : "Create Lender"}
        confirmLoading={savingLender}
        okButtonProps={{ style: { backgroundColor: primaryColor, borderColor: primaryColor } }}
        width={640}
        destroyOnHidden
      >
        <div className="space-y-4 py-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-gray-700 mb-1">Lender Name *</label>
              <Input
                className="rounded-xl"
                placeholder="e.g. HDFC Bank, Bajaj Finance"
                value={lenderModal.form.lenderName}
                onChange={(e) => setLenderField("lenderName", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Type *</label>
              <Select
                className="w-full"
                value={lenderModal.form.lenderType}
                onChange={(val) => setLenderField("lenderType", val)}
                options={LENDER_TYPES}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Contact Person</label>
              <Input
                className="rounded-xl"
                placeholder="Relationship manager name"
                value={lenderModal.form.contactPerson}
                onChange={(e) => setLenderField("contactPerson", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Phone</label>
              <Input
                className="rounded-xl"
                maxLength={10}
                placeholder="10 digit mobile number"
                value={lenderModal.form.phone}
                onChange={(e) => setLenderField("phone", e.target.value.replace(/\D/g, ""))}
              />
              {lenderModal.form.phone && !isValidPhone(lenderModal.form.phone) && (
                <p className="text-xs text-red-500 mt-1">Must be 10 digits starting with 6-9</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Email</label>
              <Input
                className="rounded-xl"
                placeholder="rm@lender.com"
                value={lenderModal.form.email}
                onChange={(e) => setLenderField("email", e.target.value)}
              />
              {lenderModal.form.email && !isValidEmail(lenderModal.form.email) && (
                <p className="text-xs text-red-500 mt-1">Enter a valid email address</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Payout TAT (Days)</label>
              <InputNumber
                className="w-full rounded-xl"
                min={0}
                max={365}
                placeholder="e.g. 30"
                value={lenderModal.form.payoutTat}
                onChange={(val) => setLenderField("payoutTat", val)}
              />
              <p className="text-xs text-gray-400 mt-1">Disbursal ke baad commission aane ke din</p>
            </div>

            <div className="flex items-end pb-1">
              <div className="flex items-center gap-3">
                <Switch
                  checked={lenderModal.form.active}
                  onChange={(val) => setLenderField("active", val)}
                  style={lenderModal.form.active ? { backgroundColor: primaryColor } : undefined}
                />
                <span className="text-xs font-bold text-gray-700">
                  {lenderModal.form.active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-gray-700 mb-1">Notes</label>
              <Input.TextArea
                rows={3}
                className="rounded-xl"
                placeholder="e.g. Login fee applicable, CIBIL cutoff 700, salaried files only..."
                value={lenderModal.form.notes}
                onChange={(e) => setLenderField("notes", e.target.value)}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* ── Products Panel (Modal) ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-gray-900 font-bold text-base">
            <Layers className="w-5 h-5 text-[#2e856e]" />
            <span>
              Products — {productsPanel.lender?.lenderName || productsPanel.lender?.name || "Lender"}
            </span>
          </div>
        }
        open={productsPanel.open}
        onCancel={closeProducts}
        footer={null}
        width={1040}
        destroyOnHidden
      >
        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              Loan products offered by this lender along with their commission slabs
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchProducts(productsPanel.lender?.id ?? productsPanel.lender?.lenderId)}
                disabled={productsLoading}
                className="p-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all"
                title="Refresh Products"
              >
                <RefreshCw className={`w-4 h-4 ${productsLoading ? "animate-spin" : ""}`} />
              </button>
              {canEdit && (
                <button
                  onClick={openCreateProduct}
                  className="flex items-center gap-2 px-3.5 py-2 bg-[#2e856e] text-white text-xs font-semibold rounded-xl shadow-sm transition-all hover:brightness-105 active:scale-95"
                  style={{ backgroundColor: primaryColor }}
                >
                  <PlusCircle className="w-4 h-4" />
                  <span>Add Product</span>
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
            {productsLoading ? (
              <div className="py-12 text-center text-gray-500">
                <RefreshCw className="w-7 h-7 mx-auto mb-2 animate-spin opacity-40" />
                <p className="text-sm font-semibold">Loading products...</p>
              </div>
            ) : productsError ? (
              <div className="py-12 px-6 text-center">
                <AlertTriangle className="w-9 h-9 mx-auto mb-3 text-red-500 opacity-80" />
                <p className="font-bold text-gray-800">Could not load products</p>
                <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">{productsError}</p>
                <button
                  onClick={() => fetchProducts(productsPanel.lender?.id ?? productsPanel.lender?.lenderId)}
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-[#2e856e] text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95"
                  style={{ backgroundColor: primaryColor }}
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Retry</span>
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                      <th className="py-3 px-4">Product</th>
                      <th className="py-3 px-4">Loan Type</th>
                      <th className="py-3 px-4">Interest Rate</th>
                      <th className="py-3 px-4">Amount Range</th>
                      <th className="py-3 px-4">Tenure</th>
                      <th className="py-3 px-4">Commission Slab</th>
                      <th className="py-3 px-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {products.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-10 text-center text-gray-400">
                          <Layers className="w-9 h-9 mx-auto mb-2 opacity-30" />
                          <p className="font-semibold">No products configured</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Add a loan product with its commission slab to start sourcing files
                          </p>
                        </td>
                      </tr>
                    ) : (
                      products.map((product) => {
                        const pid = product.id ?? product.productId;
                        const commission = product.commissionPercentage;
                        return (
                          <tr key={pid} className="hover:bg-gray-50/60 transition-colors">
                            <td className="py-3 px-4 font-semibold text-gray-900">
                              {product.productName || product.name || "—"}
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700">
                                {product.loanType || "—"}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-xs text-gray-700 whitespace-nowrap">
                              {renderRange(product.minInterestRate, product.maxInterestRate, "%")}
                            </td>
                            <td className="py-3 px-4 text-xs text-gray-700 whitespace-nowrap">
                              {product.minAmount == null && product.maxAmount == null
                                ? "—"
                                : `${formatINR(product.minAmount)} – ${formatINR(product.maxAmount)}`}
                            </td>
                            <td className="py-3 px-4 text-xs text-gray-700 whitespace-nowrap">
                              {renderRange(product.minTenure, product.maxTenure, " mo")}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              {commission === undefined || commission === null ? (
                                <span className="text-gray-400 text-xs">—</span>
                              ) : (
                                <>
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    <Percent className="w-3 h-3" />
                                    {commission}% of disbursed
                                  </span>
                                  {product.minPayout != null || product.maxPayout != null ? (
                                    <div className="text-xs text-gray-400 mt-1">
                                      Payout {formatINR(product.minPayout)} – {formatINR(product.maxPayout)}
                                    </div>
                                  ) : null}
                                </>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {canEdit && (
                                <button
                                  onClick={() => openEditProduct(product)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                  title="Edit Product"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Lender ka payout TAT — sirf tab jab backend se aaya ho */}
          {(productsPanel.lender?.payoutTat ?? productsPanel.lender?.payoutTatDays) != null && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock className="w-3.5 h-3.5" />
                <span>
                  Payout TAT: {productsPanel.lender?.payoutTat ?? productsPanel.lender?.payoutTatDays} days after disbursal
                </span>
              </div>
            )}
        </div>
      </Modal>

      {/* ── Add / Edit Product Modal ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-gray-900 font-bold text-base">
            <Layers className="w-5 h-5 text-[#2e856e]" />
            <span>{productModal.mode === "edit" ? "Edit Product" : "Add Product"}</span>
          </div>
        }
        open={productModal.open}
        onOk={handleSaveProduct}
        onCancel={() => setProductModal({ open: false, mode: "create", id: null, form: EMPTY_PRODUCT })}
        okText={productModal.mode === "edit" ? "Save Changes" : "Add Product"}
        confirmLoading={savingProduct}
        okButtonProps={{ style: { backgroundColor: primaryColor, borderColor: primaryColor } }}
        width={680}
        destroyOnHidden
      >
        <div className="space-y-4 py-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Product Name *</label>
              <Input
                className="rounded-xl"
                placeholder="e.g. Salaried Personal Loan"
                value={productModal.form.productName}
                onChange={(e) => setProductField("productName", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Loan Type *</label>
              <Select
                className="w-full"
                value={productModal.form.loanType}
                onChange={(val) => setProductField("loanType", val)}
                options={LOAN_TYPES.map((t) => ({ value: t, label: t }))}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Min Interest Rate (% p.a.)</label>
              <InputNumber
                className="w-full rounded-xl"
                min={0}
                max={100}
                step={0.25}
                value={productModal.form.minInterestRate}
                onChange={(val) => setProductField("minInterestRate", val)}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Max Interest Rate (% p.a.)</label>
              <InputNumber
                className="w-full rounded-xl"
                min={0}
                max={100}
                step={0.25}
                value={productModal.form.maxInterestRate}
                onChange={(val) => setProductField("maxInterestRate", val)}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Min Amount (₹)</label>
              <InputNumber
                className="w-full rounded-xl"
                min={0}
                value={productModal.form.minAmount}
                onChange={(val) => setProductField("minAmount", val)}
                formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Max Amount (₹)</label>
              <InputNumber
                className="w-full rounded-xl"
                min={0}
                value={productModal.form.maxAmount}
                onChange={(val) => setProductField("maxAmount", val)}
                formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Min Tenure (Months)</label>
              <InputNumber
                className="w-full rounded-xl"
                min={1}
                max={480}
                value={productModal.form.minTenure}
                onChange={(val) => setProductField("minTenure", val)}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Max Tenure (Months)</label>
              <InputNumber
                className="w-full rounded-xl"
                min={1}
                max={480}
                value={productModal.form.maxTenure}
                onChange={(val) => setProductField("maxTenure", val)}
              />
            </div>
          </div>

          {/* ── Commission Slab ── */}
          <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100">
            <div className="flex items-center gap-2 mb-3">
              <Percent className="w-4 h-4 text-emerald-700" />
              <span className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Commission Slab</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Commission (% of disbursed) *</label>
                <InputNumber
                  className="w-full rounded-xl"
                  min={0}
                  max={100}
                  step={0.05}
                  precision={2}
                  placeholder="e.g. 1.25"
                  value={productModal.form.commissionPercentage}
                  onChange={(val) => setProductField("commissionPercentage", val)}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Min Payout (₹)</label>
                <InputNumber
                  className="w-full rounded-xl"
                  min={0}
                  placeholder="Optional"
                  value={productModal.form.minPayout}
                  onChange={(val) => setProductField("minPayout", val)}
                  formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Max Payout (₹)</label>
                <InputNumber
                  className="w-full rounded-xl"
                  min={0}
                  placeholder="Optional"
                  value={productModal.form.maxPayout}
                  onChange={(val) => setProductField("maxPayout", val)}
                  formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Decimal allowed (e.g. 1.25%). Min/max payout optional hai — lender ka cap ho to bharein.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              checked={productModal.form.active}
              onChange={(val) => setProductField("active", val)}
              style={productModal.form.active ? { backgroundColor: primaryColor } : undefined}
            />
            <span className="text-xs font-bold text-gray-700">
              {productModal.form.active ? "Product Active" : "Product Inactive"}
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
}
